import { createContext, useContext, useMemo, useState } from "react";
import { useGetAirlines } from "../hooks/useGetAirlines";
import { Airline, Category, Enterprise, Place, Status, StorageLocation } from "../types";
import { useAuth } from "./AuthContext";
import { useGetCategories } from "../hooks/useGetCategories";
import { useGetEnterprise } from "../hooks/useGetEnterprise";
import { useGetStorageLocation } from "../hooks/useGetStorageLocation";
import { useGetPlaces } from "../hooks/useGetPlaces";
import { useGetStatuses } from "../hooks/useGetStatuses";

type GlobalContextType = {
	airlines: Airline[] | undefined;
	categories: Category[] | undefined;
	enterprise: Enterprise | undefined;
	storageLocations: StorageLocation[] | undefined;
	places: Place[] | undefined;
	placeKindsList: Place[] | undefined;
	placesList: Place[] | undefined;
	statuses: Status[] | undefined;

	publicUserPortal?: number;
	publicUserLanguage?: string;
};

const GlobalContext = createContext<GlobalContextType | null>(null);

export const GlobalProvider = ({ children }: { children: React.ReactNode }) => {
	const { userData } = useAuth();
	 
	const {
		data: statuses,
		isLoading: loadingStatuses,
		error: errorStatuses,
	} = useGetStatuses({ locale: userData?.user.locale });

	const {
		data: categories,
		isLoading: loadingCategories,
		error: errorCategories,
	} = useGetCategories({ locale: userData?.user.locale });

	const { data: airlines, isLoading: loadingAirlines, error: errorAirlines } = useGetAirlines();
	const { data: enterprise, isLoading: loadingEnterprises, error: errorEnterprises } = useGetEnterprise();

	const {
		data: storageLocations,
		isLoading: loadingStorageLocations,
		error: errorStorageLocations,
	} = useGetStorageLocation();

	const {
		data: places,
		isLoading: loadingPlaces,
		error: errorPlaces,
	} = useGetPlaces({ locale: userData?.user.locale });

	const { placeKindsList, placesList } = useMemo(() => {
		let placeKindsList: Place[] = [];
		let placesList: Place[] = [];

		if (places) {
			placeKindsList = places.filter((p) => p.LossPlace_fk === null);
			placesList = places.filter((p) => p.LossPlace_fk !== null);
		}

		return { placeKindsList, placesList };
	}, [places]);

	return (
		<GlobalContext.Provider
			value={{
				airlines,
				categories,
				enterprise,
				storageLocations,
				places,
				placeKindsList,
				placesList,
				statuses,
			}}
		>
			{children}
		</GlobalContext.Provider>
	);
};

export const useGlobalData = () => useContext(GlobalContext)!;
